import React, { useState, useEffect, useCallback } from 'react';
import { API_CONFIG } from '@/config/apiConfig';
import { apiService } from '@/services/apiService';
import { fetchProducts, syncProductsWithApi } from '@/services/productApiService';
import { fetchCategories, syncCategoriesWithApi } from '@/services/categoryApiService';
import { fetchPaymentMethods } from '@/services/paymentMethodApiService';
import { storeProducts, storeCategories, storePaymentMethods, getProducts, getCategories, getPaymentMethods, resetStorage, getOrders } from '@/services/storageService';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';
import { Loader2, AlertCircle, RefreshCw, Package, FileText, CreditCard, ShoppingBag, Users } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { useApp } from '@/contexts/AppContext';
import { testApiConnection } from '@/services/orderApiService';
import { getDateInBrasiliaTimezone } from '@/lib/utils';
import { syncCustomersWithApi } from '@/services/customerApiService';

const SyncManager: React.FC = () => {
  const [isProductsSyncing, setIsProductsSyncing] = useState(false);
  const [isCategoriesSyncing, setIsCategoriesSyncing] = useState(false);
  const [isPaymentMethodsSyncing, setIsPaymentMethodsSyncing] = useState(false);
  const [isOrdersSyncing, setIsOrdersSyncing] = useState(false);
  const [isCustomersSyncing, setIsCustomersSyncing] = useState(false);
  const [isAllSyncing, setIsAllSyncing] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [lastSync, setLastSync] = useState<Date | null>(null);
  const [productsCount, setProductsCount] = useState<number>(0);
  const [categoriesCount, setCategoriesCount] = useState<number>(0);
  const [paymentMethodsCount, setPaymentMethodsCount] = useState<number>(0);
  const [ordersCount, setOrdersCount] = useState<number>(0);
  const [customersCount, setCustomersCount] = useState<number>(0);
  const [pendingOrdersCount, setPendingOrdersCount] = useState<number>(0);
  const [syncStatus, setSyncStatus] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [connectionTesting, setConnectionTesting] = useState(false);
  const [syncInProgress, setSyncInProgress] = useState(false);

  const { synchronizeOrder, orders } = useApp();

  useEffect(() => {
    const storedSyncDate = localStorage.getItem('lastSyncDate');
    if (storedSyncDate) {
      setLastSync(new Date(storedSyncDate));
    }
    loadCurrentCounts();
  }, [orders]);

  const loadCurrentCounts = async () => {
    try {
      const products = await getProducts();
      const categories = await getCategories();
      const paymentMethods = await getPaymentMethods();
      const orders = await getOrders();
      
      const pendingOrders = orders.filter(order => 
        order.status === 'paid' && 
        (order.syncStatus === 'pending' || order.syncStatus === 'failed')
      );

      setProductsCount(products.length);
      setCategoriesCount(categories.length);
      setPaymentMethodsCount(paymentMethods.length);
      setOrdersCount(orders.length);
      setPendingOrdersCount(pendingOrders.length);

      // Carregar contagem de clientes
      const storedCustomers = localStorage.getItem('restaurant_customers');
      if (storedCustomers) {
        try {
          const customers = JSON.parse(storedCustomers);
          setCustomersCount(Array.isArray(customers) ? customers.length : 0);
        } catch (e) {
          setCustomersCount(0);
        }
      }

      console.log('Loaded counts:', {
        products: products.length,
        categories: categories.length,
        paymentMethods: paymentMethods.length,
        orders: orders.length,
        pendingOrders: pendingOrders.length
      });
      
      if (products.length > 0) {
        console.log('Sample product:', products[0]);
      }
      if (categories.length > 0) {
        console.log('Sample category:', categories[0]);
      }
    } catch (error) {
      console.error('Error loading counts:', error);
      setError('Erro ao carregar contagens de dados. O banco de dados pode estar corrompido.');
    }
  };

  const resetDatabaseAndSync = async () => {
    if (syncInProgress) {
      toast({
        title: "Sincronização em andamento",
        description: "Aguarde a operação atual finalizar",
        duration: 3000,
      });
      return;
    }
    
    setIsResetting(true);
    setSyncInProgress(true);
    setError(null);
    setSyncStatus('Redefinindo banco de dados...');
    
    try {
      await resetStorage();
      toast({
        title: "Banco de dados redefinido",
        description: "O banco de dados foi redefinido com sucesso. Vamos sincronizar os dados agora.",
        duration: 3000,
      });
      await syncAll();
    } catch (error) {
      console.error('Error resetting database:', error);
      setError('Erro ao redefinir o banco de dados. Tente recarregar a página.');
      toast({
        title: "Erro ao redefinir",
        description: "Ocorreu um erro ao redefinir o banco de dados.",
        variant: "destructive",
        duration: 3000,
      });
    } finally {
      setIsResetting(false);
      setSyncInProgress(false);
      setSyncStatus('');
    }
  };

  const syncProducts = useCallback(async () => {
    if (syncInProgress) {
      toast({
        title: "Sincronização em andamento",
        description: "Aguarde a conclusão da operação atual",
        duration: 3000,
      });
      return false;
    }
    
    setIsProductsSyncing(true);
    setSyncInProgress(true);
    setSyncStatus('Sincronizando produtos...');
    setError(null);
    try {
      const success = await syncProductsWithApi();
      
      if (success) {
        const products = await getProducts();
        setProductsCount(products.length);
        
        if (products.length > 0) {
          console.log('First synced product:', products[0]);
        }
        
        toast({
          title: "Produtos sincronizados",
          description: `Sincronização completa de ${products.length} produtos realizada com sucesso`,
          duration: 3000,
        });
        return true;
      } else {
        toast({
          title: "Aviso de sincronização",
          description: "Nenhum produto encontrado ou não houve alterações",
          variant: "default",
          duration: 3000,
        });
        return false;
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Ocorreu um erro inesperado";
      setError(`Erro na sincronização de produtos: ${errorMessage}`);
      toast({
        title: "Erro ao sincronizar produtos",
        description: errorMessage,
        variant: "destructive",
        duration: 3000,
      });
      return false;
    } finally {
      setIsProductsSyncing(false);
      setSyncInProgress(false);
      setSyncStatus('');
      await loadCurrentCounts();
    }
  }, [syncInProgress]);

  const syncCategories = useCallback(async () => {
    if (syncInProgress) {
      toast({
        title: "Sincronização em andamento",
        description: "Aguarde a conclusão da operação atual",
        duration: 3000,
      });
      return false;
    }
    
    setIsCategoriesSyncing(true);
    setSyncInProgress(true);
    setSyncStatus('Sincronizando categorias...');
    setError(null);
    try {
      const success = await syncCategoriesWithApi();
      
      if (success) {
        const categories = await getCategories();
        setCategoriesCount(categories.length);
        
        if (categories.length > 0) {
          console.log('First synced category:', categories[0]);
        }
        
        toast({
          title: "Categorias sincronizadas",
          description: `Sincronização completa de ${categories.length} categorias realizada com sucesso`,
          duration: 3000,
        });
        return true;
      } else {
        toast({
          title: "Aviso de sincronização",
          description: "Nenhuma categoria encontrada ou não houve alterações",
          variant: "default",
          duration: 3000,
        });
        return false;
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Ocorreu um erro inesperado";
      setError(`Erro na sincronização de categorias: ${errorMessage}`);
      toast({
        title: "Erro ao sincronizar categorias",
        description: errorMessage,
        variant: "destructive",
        duration: 3000,
      });
      return false;
    } finally {
      setIsCategoriesSyncing(false);
      setSyncInProgress(false);
      setSyncStatus('');
      await loadCurrentCounts();
    }
  }, [syncInProgress]);

  const syncPaymentMethods = useCallback(async () => {
    if (syncInProgress) {
      toast({
        title: "Sincronização em andamento",
        description: "Aguarde a conclusão da operação atual",
        duration: 3000,
      });
      return false;
    }
    
    setIsPaymentMethodsSyncing(true);
    setSyncInProgress(true);
    setSyncStatus('Sincronizando formas de pagamento...');
    setError(null);
    
    try {
      console.log('Fetching payment methods...');
      const paymentMethods = await fetchPaymentMethods(true);
      console.log(`Fetched ${paymentMethods.length} payment methods`);

      if (paymentMethods.length > 0) {
        try {
          console.log('Storing payment methods...');
          await storePaymentMethods(paymentMethods);
          console.log('Payment methods stored successfully');
          
          setPaymentMethodsCount(paymentMethods.length);
          toast({
            title: "Formas de Pagamento sincronizadas",
            description: `Sincronização completa de ${paymentMethods.length} formas de pagamento realizada com sucesso`,
            duration: 3000,
          });
          return true;
        } catch (storageError) {
          console.error('Error storing payment methods:', storageError);
          setError(`Erro ao armazenar formas de pagamento: ${storageError instanceof Error ? storageError.message : "Erro desconhecido"}`);
          try {
            localStorage.setItem('payment_methods', JSON.stringify(paymentMethods));
            toast({
              title: "Formas de Pagamento parcialmente sincronizadas",
              description: "Armazenado conjunto reduzido devido a limitações de espaço",
            });
            return true;
          } catch (localStorageError) {
            console.error('Error with localStorage fallback:', localStorageError);
            throw new Error('Failed to store payment methods anywhere');
          }
        }
      } else {
        toast({
          title: "Nenhuma forma de pagamento encontrada",
          description: "A API não retornou formas de pagamento",
          variant: "default",
          duration: 3000,
        });
        return false;
      }
    } catch (error) {
      console.error('Error in payment methods sync:', error);
      const errorMessage = error instanceof Error ? error.message : "Ocorreu um erro inesperado";
      setError(`Erro na sincronização de formas de pagamento: ${errorMessage}`);
      toast({
        title: "Erro ao sincronizar formas de pagamento",
        description: errorMessage,
        variant: "destructive",
        duration: 3000,
      });
      return false;
    } finally {
      setIsPaymentMethodsSyncing(false);
      setSyncInProgress(false);
      setSyncStatus('');
      await loadCurrentCounts();
    }
  }, [syncInProgress]);

  const syncCustomers = useCallback(async () => {
    if (syncInProgress) {
      toast({
        title: "Sincronização em andamento",
        description: "Aguarde a conclusão da operação atual",
        duration: 3000,
      });
      return false;
    }
    
    setIsCustomersSyncing(true);
    setSyncInProgress(true);
    setSyncStatus('Sincronizando clientes...');
    setError(null);
    
    try {
      console.log('Iniciando sincronização de clientes');
      const success = await syncCustomersWithApi();
      
      if (success) {
        // Atualiza a contagem de clientes
        const storedCustomers = localStorage.getItem('restaurant_customers');
        if (storedCustomers) {
          try {
            const customers = JSON.parse(storedCustomers);
            setCustomersCount(Array.isArray(customers) ? customers.length : 0);
          } catch (e) {
            setCustomersCount(0);
          }
        }
        
        toast({
          title: "Clientes sincronizados",
          description: `Sincronização de clientes realizada com sucesso`,
          duration: 3000,
        });
        return true;
      } else {
        toast({
          title: "Aviso de sincronização",
          description: "Nenhum cliente encontrado ou não houve alterações",
          variant: "default",
          duration: 3000,
        });
        return false;
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Ocorreu um erro inesperado";
      setError(`Erro na sincronização de clientes: ${errorMessage}`);
      toast({
        title: "Erro ao sincronizar clientes",
        description: errorMessage,
        variant: "destructive",
        duration: 3000,
      });
      return false;
    } finally {
      setIsCustomersSyncing(false);
      setSyncInProgress(false);
      setSyncStatus('');
      await loadCurrentCounts();
    }
  }, [syncInProgress]);

  const syncPendingOrders = async () => {
    if (syncInProgress) {
      toast({
        title: "Sincronização em andamento",
        description: "Aguarde a conclusão da operação atual",
        duration: 3000,
      });
      return false;
    }
    
    setIsOrdersSyncing(true);
    setSyncInProgress(true);
    setSyncStatus('Sincronizando pedidos pendentes...');
    setError(null);
    
    try {
      const orders = await getOrders();
      
      const pendingOrders = orders.filter(order => 
        order.status === 'paid' && 
        (order.syncStatus === 'pending' || order.syncStatus === 'failed')
      );
      
      if (pendingOrders.length === 0) {
        toast({
          title: "Sem pedidos pendentes",
          description: "Não há pedidos pendentes para sincronizar",
          duration: 3000,
        });
        return false;
      }
      
      // For manual sync in settings, only try one order at a time
      if (pendingOrders.length > 0) {
        const order = pendingOrders[0];
        const success = await synchronizeOrder(order.id);
        
        if (success) {
          toast({
            title: "Sincronização de pedido completa",
            description: `O pedido foi sincronizado com sucesso. ${pendingOrders.length - 1} pendentes.`,
            duration: 3000,
          });
        } else {
          toast({
            title: "Falha na sincronização",
            description: `Não foi possível sincronizar o pedido. Erro: ${order.syncError || 'Desconhecido'}`,
            variant: "destructive",
            duration: 3000,
          });
        }
        
        return success;
      }
      
      return false;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Ocorreu um erro inesperado";
      setError(`Erro na sincronização de pedidos: ${errorMessage}`);
      toast({
        title: "Erro ao sincronizar pedidos",
        description: errorMessage,
        variant: "destructive",
        duration: 3000,
      });
      return false;
    } finally {
      setIsOrdersSyncing(false);
      setSyncInProgress(false);
      setSyncStatus('');
      await loadCurrentCounts();
    }
  };

  const syncAll = async () => {
    if (syncInProgress) {
      toast({
        title: "Sincronização em andamento",
        description: "Aguarde a conclusão da operação atual",
        duration: 3000,
      });
      return;
    }
    
    setIsAllSyncing(true);
    setIsProductsSyncing(true);
    setIsCategoriesSyncing(true);
    setIsPaymentMethodsSyncing(true);
    setIsOrdersSyncing(true);
    setIsCustomersSyncing(true);
    setSyncInProgress(true);
    setError(null);
    
    try {
      setSyncStatus('Iniciando sincronização completa...');
      console.log('Starting full sync process...');
      
      setSyncStatus('Sincronizando produtos...');
      const productsResult = await syncProductsWithApi();
      
      setSyncStatus('Sincronizando categorias...');
      const categoriesResult = await syncCategoriesWithApi();
      
      setSyncStatus('Sincronizando formas de pagamento...');
      const paymentsResult = await syncPaymentMethods();
      
      setSyncStatus('Sincronizando clientes...');
      const customersResult = await syncCustomers();
      
      setSyncStatus('Sincronizando pedidos pendentes...');
      const ordersResult = await syncPendingOrders();
      
      const succeededProducts = productsResult;
      const succeededCategories = categoriesResult;
      const succeededPayments = paymentsResult;
      const succeededCustomers = customersResult;
      const succeededOrders = ordersResult;
      
      const successParts = [];
      if (succeededProducts) successParts.push('produtos');
      if (succeededCategories) successParts.push('categorias');
      if (succeededPayments) successParts.push('formas de pagamento');
      if (succeededCustomers) successParts.push('clientes');
      if (succeededOrders) successParts.push('pedidos pendentes');
      
      const now = getDateInBrasiliaTimezone();
      localStorage.setItem('lastSyncDate', now.toISOString());
      setLastSync(now);
      
      await loadCurrentCounts();
      
      if (successParts.length > 0) {
        toast({
          title: "Sincronização completa",
          description: `Sincronização de ${successParts.join(', ')} realizada com sucesso`,
          duration: 3000,
        });
      } else {
        toast({
          title: "Sincronização falhou",
          description: "Nenhum dado foi sincronizado com sucesso",
          variant: "destructive",
          duration: 3000,
        });
      }
    } catch (error) {
      console.error('Error during sync all:', error);
      const errorMessage = error instanceof Error ? error.message : "Ocorreu um erro inesperado";
      setError(`Erro na sincronização completa: ${errorMessage}`);
      toast({
        title: "Erro ao sincronizar",
        description: errorMessage,
        variant: "destructive",
        duration: 3000,
      });
    } finally {
      setSyncStatus('');
      setIsAllSyncing(false);
      setIsProductsSyncing(false);
      setIsCategoriesSyncing(false);
      setIsPaymentMethodsSyncing(false);
      setIsOrdersSyncing(false);
      setIsCustomersSyncing(false);
      setSyncInProgress(false);
    }
  };

  const handleTestConnection = async () => {
    if (syncInProgress || connectionTesting) {
      toast({
        title: "Operação em andamento",
        description: "Aguarde a conclusão da operação atual",
        duration: 3000,
      });
      return;
    }
    
    setConnectionTesting(true);
    try {
      const result = await testApiConnection();
      if (result) {
        toast({
          title: "Conexão bem-sucedida",
          description: "A conexão com a API foi estabelecida com sucesso.",
        });
      } else {
        toast({
          title: "Falha na conexão",
          description: "Não foi possível estabelecer conexão com a API.",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Erro ao testar conexão",
        description: error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
    } finally {
      setConnectionTesting(false);
    }
  };

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold">Sincronização de Dados</h2>
      
      {lastSync && (
        <p className="text-sm text-muted-foreground">
          Última sincronização: {lastSync.toLocaleDateString()} {lastSync.toLocaleTimeString()}
        </p>
      )}

      {error && (
        <Alert className="bg-red-50 border-red-300">
          <AlertCircle className="h-4 w-4 text-red-600" />
          <AlertTitle>Erro</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {syncStatus && (
        <Alert className="bg-blue-50 border-blue-200">
          <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
          <AlertTitle>Sincronização em andamento</AlertTitle>
          <AlertDescription>{syncStatus}</AlertDescription>
        </Alert>
      )}

      <div className="flex flex-col gap-2">
        <div className="text-sm">
          <span className="font-medium">Dados atuais: </span>
          <span className="text-muted-foreground">
            {productsCount} produtos, {categoriesCount} categorias, {customersCount} clientes, {paymentMethodsCount} formas de pagamento, {ordersCount} pedidos
            {pendingOrdersCount > 0 && (
              <span className="ml-1 text-amber-600 font-medium">({pendingOrdersCount} pedidos pendentes de sincronização)</span>
            )}
          </span>
        </div>
        
        {(productsCount === 0 || categoriesCount === 0) && (
          <Alert className="bg-amber-50 border-amber-200">
            <AlertCircle className="h-4 w-4 text-amber-600" />
            <AlertTitle>Atenção</AlertTitle>
            <AlertDescription>
              Você {productsCount === 0 ? 'não tem produtos' : ''}{productsCount === 0 && categoriesCount === 0 ? ' e ' : ''}{categoriesCount === 0 ? 'não tem categorias' : ''} sincronizados. 
              Por favor, sincronize os dados para poder usar o sistema corretamente.
            </AlertDescription>
          </Alert>
        )}
        
        {pendingOrdersCount > 0 && (
          <Alert className="bg-yellow-50 border-yellow-200">
            <ShoppingBag className="h-4 w-4 text-yellow-600" />
            <AlertTitle>Pedidos pendentes de sincronização</AlertTitle>
            <AlertDescription className="flex items-center justify-between">
              <span>Há {pendingOrdersCount} pedidos aguardando sincronização com o ERP.</span>
              <Button 
                size="sm" 
                variant="outline" 
                className="border-yellow-300 hover:bg-yellow-100 mt-1"
                onClick={syncPendingOrders}
                disabled={isOrdersSyncing || isAllSyncing}
              >
                {isOrdersSyncing ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : <RefreshCw className="mr-2 h-3 w-3" />}
                Sincronizar Pedidos
              </Button>
            </AlertDescription>
          </Alert>
        )}
      </div>

      <div className="border rounded-md p-4">
        <h3 className="text-lg font-medium">Sincronização com o Sistema</h3>
        <p className="text-sm text-muted-foreground">
          API configurada e pronta para uso
        </p>
        <p className="text-sm text-muted-foreground mb-4">
          Baixe as informações mais recentes do sistema e armazene-as localmente.
        </p>
        {API_CONFIG.apiKey && (
          <p className="text-xs text-muted-foreground mb-4">
            Usando token do HeaderToken: {API_CONFIG.apiKey.substring(0, 15)}...
          </p>
        )}
        <div className="flex flex-col sm:flex-row gap-2">
          <Button 
            className="flex-1" 
            onClick={syncAll} 
            disabled={isAllSyncing || isProductsSyncing || isCategoriesSyncing || isPaymentMethodsSyncing || isOrdersSyncing || isCustomersSyncing || isResetting}
          >
            {isAllSyncing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Sincronizar com o Sistema
          </Button>
          <Button 
            variant="outline" 
            onClick={handleTestConnection} 
            disabled={isAllSyncing || isResetting}
            className="sm:w-auto"
          >
            Testar Conexão
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="border rounded-md p-4">
          <h3 className="text-lg font-medium flex items-center">
            <Package className="h-4 w-4 mr-2 text-blue-600" />
            Produtos
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            Sincronize os produtos com a base de dados mais recente.
          </p>
          <Button 
            onClick={syncProducts} 
            disabled={isProductsSyncing || isAllSyncing || isResetting} 
            className="w-full"
          >
            {isProductsSyncing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Sincronizar Produtos
          </Button>
        </div>

        <div className="border rounded-md p-4">
          <h3 className="text-lg font-medium flex items-center">
            <FileText className="h-4 w-4 mr-2 text-green-600" />
            Categorias
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            Sincronize as categorias com a base de dados mais recente.
          </p>
          <Button 
            onClick={syncCategories} 
            disabled={isCategoriesSyncing || isAllSyncing || isResetting} 
            className="w-full"
          >
            {isCategoriesSyncing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Sincronizar Categorias
          </Button>
        </div>
        
        <div className="border rounded-md p-4">
          <h3 className="text-lg font-medium flex items-center">
            <Users className="h-4 w-4 mr-2 text-indigo-600" />
            Clientes
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            Sincronize os clientes com a base de dados mais recente.
          </p>
          <Button 
            onClick={syncCustomers} 
            disabled={isCustomersSyncing || isAllSyncing || isResetting} 
            className="w-full"
          >
            {isCustomersSyncing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Sincronizar Clientes
          </Button>
        </div>
        
        <div className="border rounded-md p-4">
          <h3 className="text-lg font-medium flex items-center">
            <CreditCard className="h-4 w-4 mr-2 text-purple-600" />
            Formas de Pagamento
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            Sincronize as formas de pagamento com a base de dados mais recente.
          </p>
          <Button 
            onClick={syncPaymentMethods} 
            disabled={isPaymentMethodsSyncing || isAllSyncing || isResetting} 
            className="w-full"
          >
            {isPaymentMethodsSyncing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Sincronizar Formas de Pagamento
          </Button>
        </div>
        
        <div className="border rounded-md p-4">
          <h3 className="text-lg font-medium flex items-center">
            <ShoppingBag className="h-4 w-4 mr-2 text-amber-600" />
            Pedidos Pendentes
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            Sincronize os pedidos pendentes com o ERP.
          </p>
          <Button 
            onClick={syncPendingOrders} 
            disabled={isOrdersSyncing || isAllSyncing || isResetting} 
            className="w-full"
            variant={pendingOrdersCount > 0 ? "default" : "outline"}
          >
            {isOrdersSyncing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Sincronizar {pendingOrdersCount > 0 ? `${pendingOrdersCount} Pedidos Pendentes` : 'Pedidos'}
          </Button>
        </div>
      </div>

      <div className="border border-red-200 rounded-md p-4 bg-red-50">
        <h3 className="text-lg font-medium text-red-800">Restaurar Banco de Dados</h3>
        <p className="text-sm text-red-600 mb-4">
          Utilize essa opção caso esteja enfrentando problemas de sincronização ou se os dados não estão aparecendo corretamente.
          Isso irá redefinir o banco de dados local e depois sincronizar com o sistema.
        </p>
        <Button 
          variant="destructive" 
          onClick={resetDatabaseAndSync} 
          disabled={isResetting || isAllSyncing}
          className="w-full"
        >
          {isResetting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {!isResetting ? (
            <>
              <RefreshCw className="mr-2 h-4 w-4" />
              Restaurar e Sincronizar Dados
            </>
          ) : 'Restaurando...'}
        </Button>
      </div>
    </div>
  );
};

export default SyncManager;
